import React from 'react';
import { ActivityIndicator, Image, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';

import { Typography } from '../ui';
import { EventDiscussButton } from '../chat/EventDiscussButton';
import { getFileUrl } from '../../services/api';
import { formatEventDuration } from '../../utils/eventDuration';
import { eventTypeLabel, resolveEventTypeFromBeKind } from '../../utils/eventTypeLabels';
import { LivesCalendarItem, getCalendarItemDiscussionRef } from '../../hooks/useLivesCalendar';

import { accentTextOn, inkOnFill, ON_ACCENT_TEXT } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';
import { AgapellaBadge, badgeGap } from '../subscription/AgapellaBadge';
type LivesWeekEventCardProps = {
  accentColor: string;
  isDark: boolean;
  isEnded: boolean;
  isLive: boolean;
  isMobile: boolean;
  isPending: boolean;
  isReminded: boolean;
  item: LivesCalendarItem;
  language: string;
  onPress: () => void;
  onPrimary: () => void;
  onRemove?: () => void;
  onReport: () => void;
  /** CORE-1693 — репост в свою ленту; нет у комнат. */
  onRepost?: () => void;
  /**
   * CORE-879 — нет у Live-сессии программы, ссылка на которую у получателя
   * не откроется (screens/live/liveShareGate). Тогда нет и репоста.
   */
  onShare?: () => void;
  /**
   * CORE-879 — почему событие в календаре, когда это не «Я иду», а покупка:
   * Live-сессия купленной программы. Без подписи отсутствие «Убрать» и
   * неотключаемая кнопка читались бы как поломка.
   */
  sourceNote?: string;
  /** CORE-879 — подпись основной кнопки вместо «Присоединиться / Я иду». */
  primaryLabel?: string;
  themeColors: {
    surfacePrimary: string;
    textPrimary: string;
    textSecondary: string;
  };
};

const IMAGE_W = 150;

const formatClock = (date: Date, language: string) =>
  date.toLocaleTimeString(language === 'ru' ? 'ru-RU' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

const spotsLabel = (n: number, language: string) => {
  if (language !== 'ru') return `${n} ${n === 1 ? 'spot' : 'spots'} left`;
  const abs = n % 100;
  const tail = abs % 10;
  const word = abs > 10 && abs < 20 ? 'мест' : tail === 1 ? 'место' : tail > 1 && tail < 5 ? 'места' : 'мест';
  return `Осталось ${n} ${word}`;
};

/**
 * CORE-1134 — карточка события в недельной афише. Горизонтальная по макету:
 * обложка слева, справа время и длительность, название, мастер с направлением
 * и основное действие. На телефоне обложка встаёт сверху. Данные и действия
 * те же, что были у карточек календаря (чат, поделиться, пожаловаться,
 * «Присоединиться / Я иду»); для офлайн-событий добавлены стоимость и места.
 *
 * CORE-1696 — переход в обсуждение события стал полноценной кнопкой
 * «Обсудить» рядом с «Присоединиться» (вместо малозаметной иконки в шапке):
 * на desktop обе кнопки в одном ряду под строкой мастера, на телефоне —
 * во всю ширину друг под другом. «Присоединиться» в чат не ведёт.
 */
export const LivesWeekEventCard: React.FC<LivesWeekEventCardProps> = ({
  accentColor,
  isDark,
  isEnded,
  isLive,
  isMobile,
  isPending,
  isReminded,
  item,
  language,
  onPress,
  onPrimary,
  onRemove,
  onReport,
  onRepost,
  onShare,
  sourceNote,
  primaryLabel: primaryLabelOverride,
  themeColors,
}) => {
  const ru = language === 'ru';
  const start = new Date(item.startDateTimeUtc);
  // CORE-225 — у многодневного события «конец» в тот же час другого дня
  // выглядит как «14:30 – 14:30»; для него показываем только начало, а срок
  // и так виден в чипе длительности («14 дней»).
  const isMultiDay = (item.durationMinutes ?? 0) >= 24 * 60;
  const end = item.durationMinutes && !isMultiDay ? new Date(start.getTime() + item.durationMinutes * 60_000) : null;
  const timeLabel = end ? `${formatClock(start, language)} – ${formatClock(end, language)}` : formatClock(start, language);
  const coverUrl = getFileUrl(item.coverImageUrl) || null;
  const offline = item._offlineEvent;
  const isOffline = !!offline;

  const displayCategory = (item.categories && item.categories.length > 0)
    ? item.categories.join(' · ')
    : (item.category || '');

  // AMB-220 — подписи типов из FE-словаря, не из legacy-лейблов BE.
  const beKind = item.kind;
  const beLabel = beKind ? ((ru ? beKind.labelRu : beKind.labelEn) || beKind.labelEn || beKind.labelRu || '') : '';
  const resolvedType = resolveEventTypeFromBeKind(beKind?.code);
  const kindLabel = isOffline
    ? (ru ? 'Офлайн' : 'Offline')
    : resolvedType ? eventTypeLabel(resolvedType, language as 'ru' | 'en') : beLabel;

  const priceLabel = isOffline
    ? (offline?.price ? String(offline.price) : (ru ? 'Бесплатно' : 'Free'))
    : null;
  const spots = offline?.spotsLeft != null ? spotsLabel(offline.spotsLeft, language) : null;

  const ghostBtn = {
    backgroundColor: isDark ? 'rgba(255,244,237,0.055)' : 'rgba(255,250,247,0.94)',
    borderColor: isDark ? 'rgba(255,176,128,0.24)' : 'rgba(166,126,101,0.22)',
  };

  const primaryLabel = isEnded
    ? (ru ? 'Завершено' : 'Ended')
    : primaryLabelOverride
      ? primaryLabelOverride
      : isReminded
        ? (ru ? 'Я иду' : "I'm going")
        : (ru ? 'Присоединиться' : 'Join');

  // CORE-1696 — открывает вкладку «Чаты мастера» сразу на обсуждении этого
  // события; доступно и без участия в событии. Прячется сам, когда у события
  // нет мастера, к которому можно привести (CORE-1264).
  const discussButton = (
    <EventDiscussButton
      {...getCalendarItemDiscussionRef(item)}
      masterName={item.master?.name}
      label={ru ? 'Обсудить' : 'Discuss'}
      isDark={isDark}
      accentColor={accentTextOn(accentColor)}
      textColor={themeColors.textPrimary}
      fullWidth={isMobile}
      style={!isMobile ? { flex: 1, minWidth: 0 } : undefined}
      testID={`lives-week-discuss-${item.id}`}
    />
  );

  return (
    <TouchableOpacity
      testID={`lives-week-card-${item.id}`}
      activeOpacity={0.92}
      onPress={onPress}
      style={[
        styles.card,
        // CORE-1733 (возврат QA 23.10) — карточка события в /lives, /schedule,
        // /upcoming — поверхность v4 на вебе (была плоская rgba .78 / .92).
        Platform.OS === 'web' ? surfaceFill(isDark ? 'dark' : 'light') : {
          backgroundColor: isDark ? 'rgba(30,29,31,0.92)' : themeColors.surfacePrimary,
          borderColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
        },
        {
          flexDirection: isMobile ? 'column' : 'row',
          opacity: isEnded ? 0.62 : 1,
        },
      ]}
    >
      <View style={[styles.imageWrap, isMobile ? { width: '100%', height: 140 } : { width: IMAGE_W, alignSelf: 'stretch' }]}>
        {coverUrl ? (
          <Image source={{ uri: coverUrl }} style={styles.image} />
        ) : (
          <View
            style={[
              styles.image,
              { alignItems: 'center', justifyContent: 'center', backgroundColor: '#E85D04' },
              Platform.OS === 'web' && { backgroundImage: 'linear-gradient(135deg, #E85D04 0%, #181411 100%)' } as any,
            ]}
          >
            <Ionicons name={isOffline ? 'location-outline' : 'videocam-outline'} size={36} color="rgba(255,255,255,0.7)" />
          </View>
        )}
        <View style={styles.imageOverlay} />
        {kindLabel ? (
          <View style={[styles.kindBadge, { backgroundColor: isOffline ? accentColor : 'rgba(0,0,0,0.6)' }]}>
            <Typography variant="caption" style={{ color: isOffline ? inkOnFill(accentColor) : '#FFF' }}>{kindLabel}</Typography>
          </View>
        ) : null}
        {onRemove && (
          <TouchableOpacity
            onPress={(e) => { e.stopPropagation(); onRemove(); }}
            activeOpacity={0.8}
            accessibilityLabel={ru ? 'Убрать из моих событий' : 'Remove from my events'}
            style={styles.removeBtn}
          >
            <Ionicons name="close" size={16} color="#FFF" />
          </TouchableOpacity>
        )}
      </View>

      <View style={[styles.body, isMobile && { padding: 14, gap: 10 }]}>
        <View style={styles.metaRow}>
          {isLive ? (
            <View style={styles.liveChip}>
              <View style={styles.liveDot} />
              <Typography variant="caption" style={{ color: '#FFF' }}>LIVE</Typography>
            </View>
          ) : (
            <Typography variant="overline" style={{ color: themeColors.textSecondary }}>{timeLabel}</Typography>
          )}
          <View style={styles.metaRight}>
            {item.durationMinutes ? (
              <View style={styles.durationChip}>
                <Ionicons name="time-outline" size={12} color={themeColors.textSecondary} />
                <Typography variant="caption" style={{ color: themeColors.textSecondary }}>
                  {formatEventDuration(item.durationMinutes)}
                </Typography>
              </View>
            ) : null}
            {onRepost && (
              <TouchableOpacity
                testID={`lives-repost-${item.id}`}
                style={[styles.iconBtn, ghostBtn]}
                onPress={(e) => { e.stopPropagation(); onRepost(); }}
                accessibilityLabel={ru ? 'Репост в свою ленту' : 'Repost to your feed'}
              >
                <Ionicons name="repeat-outline" size={15} color={themeColors.textPrimary} />
              </TouchableOpacity>
            )}
            {onShare && (
              <TouchableOpacity
                testID={`lives-share-${item.id}`}
                style={[styles.iconBtn, ghostBtn]}
                onPress={(e) => { e.stopPropagation(); onShare(); }}
                accessibilityLabel={ru ? 'Поделиться' : 'Share'}
              >
                <Ionicons name="share-social-outline" size={15} color={themeColors.textPrimary} />
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={[styles.iconBtn, ghostBtn]}
              onPress={(e) => { e.stopPropagation(); onReport(); }}
              accessibilityLabel="Пожаловаться"
            >
              <Ionicons name="flag-outline" size={15} color={themeColors.textPrimary} />
            </TouchableOpacity>
          </View>
        </View>

        <AgapellaBadge access={item.access} style={badgeGap} testID={`agapella-badge-live-${item.id}`} />
        <Typography variant="h5" numberOfLines={2} style={{ color: themeColors.textPrimary }}>
          {item.title}
        </Typography>

        {sourceNote ? (
          <View testID={`lives-week-source-${item.id}`} style={styles.sourceNote}>
            <Ionicons name="bookmark-outline" size={13} color={accentColor} />
            <Typography variant="caption" numberOfLines={1} style={{ color: themeColors.textSecondary, flexShrink: 1 }}>
              {sourceNote}
            </Typography>
          </View>
        ) : null}

        {(priceLabel || spots) ? (
          <Typography variant="caption" numberOfLines={1} style={{ color: themeColors.textSecondary }}>
            {[priceLabel, spots].filter(Boolean).join(' · ')}
          </Typography>
        ) : null}

        <View style={styles.footer}>
          <View style={styles.master}>
            {item.master?.photoUrl ? (
              <Image source={{ uri: getFileUrl(item.master.photoUrl) || undefined }} style={styles.avatar} />
            ) : (
              <View style={[styles.avatar, { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', alignItems: 'center', justifyContent: 'center' }]}>
                <Ionicons name="person-outline" size={16} color={themeColors.textSecondary} />
              </View>
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Typography variant="overline" numberOfLines={1} style={{ color: themeColors.textPrimary }}>
                {item.master?.name}
              </Typography>
              {displayCategory ? (
                <Typography variant="caption" numberOfLines={1} style={{ color: themeColors.textSecondary }}>
                  {displayCategory}
                </Typography>
              ) : null}
            </View>
          </View>

          {/* CORE-1696 — участие и обсуждение: две кнопки рядом на desktop,
              друг под другом во всю ширину на телефоне. Порядок на телефоне —
              сначала основная «Присоединиться», затем контурная «Обсудить». */}
          <View style={[styles.actions, isMobile && styles.actionsStacked]}>
            {!isMobile ? discussButton : null}
            <TouchableOpacity
              testID={`lives-week-cta-${item.id}`}
              disabled={isPending || isEnded}
              onPress={(e) => { e.stopPropagation(); onPrimary(); }}
              style={[
                styles.cta,
                isMobile ? { width: '100%' } : { flex: 1, minWidth: 0 },
                isEnded
                  ? { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', borderWidth: 1, borderColor: 'transparent' }
                  : isReminded
                    ? { ...ghostBtn, borderWidth: 1 }
                    : {
                        backgroundColor: '#F45122',
                        borderWidth: 1,
                        borderColor: '#F26A36',
                        ...(Platform.OS === 'web' && {
                          backgroundImage: 'linear-gradient(100deg, #F45122 0%, #FF955D 100%)',
                          boxShadow: '0 8px 20px rgba(244,81,34,0.22)',
                        } as any),
                      },
              ]}
            >
              {isPending ? (
                <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
              ) : isReminded && !isEnded ? (
                <Ionicons name="checkmark" size={16} color={themeColors.textPrimary} />
              ) : null}
              <Typography
                variant="overline"
                numberOfLines={1}
                style={{ color: isEnded ? themeColors.textSecondary : isReminded ? themeColors.textPrimary : ON_ACCENT_TEXT }}
              >
                {primaryLabel}
              </Typography>
            </TouchableOpacity>
            {isMobile ? discussButton : null}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 20,
    borderWidth: 1,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.06,
    shadowRadius: 20,
    elevation: 4,
    ...Platform.select({ web: { transition: 'transform 0.2s ease, box-shadow 0.2s ease' } as any }),
  },
  imageWrap: {
    position: 'relative',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  imageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  kindBadge: {
    position: 'absolute',
    top: 10,
    left: 10,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 7,
    ...Platform.select({ web: { backdropFilter: 'blur(4px)' } as any }),
  },
  removeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer' } as any }),
  },
  body: {
    flex: 1,
    minWidth: 0,
    padding: 16,
    gap: 10,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  metaRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  durationChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    height: 28,
    borderRadius: 8,
    marginRight: 2,
  },
  liveChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    height: 28,
    borderRadius: 8,
    backgroundColor: '#E53E3E',
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: '#FFF',
  },
  sourceNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  iconBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
  },
  // CORE-1696 — строка мастера, под ней ряд действий (как на макете): две
  // подписанные кнопки рядом с именем мастера в одну строку не помещаются.
  footer: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
    marginTop: 'auto',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionsStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 8,
  },
  master: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  cta: {
    height: 40,
    borderRadius: 999,
    paddingHorizontal: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'all 0.2s ease' } as any }),
  },
});
